import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import API from "../Api/Api";
import { ENDPOINTS } from "../Api/Endpoints";
import { tokenManager } from "../Api/tokenManager";
import PatientBottomNav from "./PatientBottomNav";
import { PatientShellChromeContext } from "./patientShellChrome";
import { usePatientShellScrollHide } from "./usePatientShellScrollHide";
import "./PatientAppShell.scss";

/** App chrome (bottom nav + shell padding) for signed-in patient app routes. */
function isPatientAppReady(user) {
  if (!user) return false;
  return Boolean(user.isProfileComplete);
}

export default function PatientAppShell() {
  const { pathname } = useLocation();
  const pathNorm = pathname.replace(/\/$/, "") || "/";
  /** Last segment only — supports Router `basename` (e.g. /app/appointment-confirmed). */
  const pathLastSegment = pathNorm.split("/").filter(Boolean).pop() || "";
  const authToken = useSelector((s) => s.auth?.token);
  const profileSuccess = useSelector((s) => s.auth?.profileSuccess);
  const [bottomNavHidden, setBottomNavHidden] = useState(false);
  /** When true, pages (e.g. chat thread) remove the app bar from layout entirely. */
  const [appBarOverlayHidden, setAppBarHidden] = useState(false);
  const chromeScrollHidden = usePatientShellScrollHide(pathname);

  /** Hide tab bar synchronously (effects run too late): support chat composer, linear request wizard steps. */
  const requestWizardNavHidden =
    /^\/request\/[^/]+\/(upload|category|details|review)(?:\/|$)/i.test(pathname);
  /** Care price uses PatientAppLayout’s top bar and full vertical space (matches wizard-style flows). */
  const carePriceNavHidden = pathname.startsWith("/care-price");
  /** Appointments list + detail: full viewport under top bar, no bottom tab bar (mobile / tablet). */
  const appointmentsNavHidden =
    pathLastSegment === "appointment" || pathLastSegment === "appointment-confirmed";
  const bottomNavHiddenForPath =
    pathname.startsWith("/patient-support") ||
    requestWizardNavHidden ||
    carePriceNavHidden ||
    appointmentsNavHidden;
  const chromeBottomNavHidden = bottomNavHidden || bottomNavHiddenForPath;

  const shellChrome = useMemo(
    () => ({
      bottomNavHidden: chromeBottomNavHidden,
      setBottomNavHidden,
      appBarHidden: appBarOverlayHidden,
      setAppBarHidden,
      chromeScrollHidden,
      appBarScrollCollapsed: chromeScrollHidden,
    }),
    [chromeBottomNavHidden, appBarOverlayHidden, chromeScrollHidden]
  );

  const [meChecked, setMeChecked] = useState(
    () => !tokenManager.getTokenForRole("patient")
  );
  const [appMode, setAppMode] = useState(false);

  useEffect(() => {
    const token = tokenManager.getTokenForRole("patient");
    if (!token) {
      setAppMode(false);
      setMeChecked(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await API.get(ENDPOINTS.GET_ME, {
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });
        const body = res?.data ?? res;
        const user = body?.user ?? body?.data?.user ?? null;
        if (!cancelled) {
          setAppMode(isPatientAppReady(user));
          setMeChecked(true);
        }
      } catch {
        if (!cancelled) {
          // Keep app chrome for signed-in patients even if GET_ME momentarily fails.
          setAppMode(true);
          setMeChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname, authToken, profileSuccess]);

  if (!meChecked) {
    return <Outlet />;
  }

  if (!appMode) {
    return <Outlet />;
  }

  return (
    <PatientShellChromeContext.Provider value={shellChrome}>
      <div
        className={`patient-app-shell${chromeBottomNavHidden ? " patient-app-shell--no-bottom-nav" : ""}${
          appBarOverlayHidden ? " patient-app-shell--no-app-bar" : ""
        }`.trim()}
      >
        <main className="patient-app-shell__outlet">
          <Outlet />
        </main>
        {!chromeBottomNavHidden ? <PatientBottomNav /> : null}
      </div>
    </PatientShellChromeContext.Provider>
  );
}
